// ProfilePicturePicker.tsx — the one place a person chooses their picture: upload a file (the shared Upload
// component, with its size and type checks), paste a link to an image on any web site, or remove the picture.
// The sign-up page and the Account section of the settings panel both render this, so they offer the same
// options and behave the same way. It only reports the choice: onChange(File | link | null for removed).
import React, { useState } from 'react';
import { Upload } from './Upload';
import '../../styles/ui-buttons.scss';
import { parseAvatarLink } from '../botFactoryAPI';

type ProfilePicturePickerProps = {
  onChange: (pic: File | string | null) => void;
  disabled?: boolean;
  /** Whether a picture is set now; the Remove button shows only then. */
  hasPicture?: boolean;
  /** Shown at the left of the Upload and Remove buttons (the Account section puts the current picture here). */
  leading?: React.ReactNode;
  /** Show the chosen file under the Upload button (sign-up does; the Account section shows its own picture). */
  showFilePreview?: boolean;
};

export function ProfilePicturePicker({ onChange, disabled = false, hasPicture = false, leading, showFilePreview = true }: ProfilePicturePickerProps) {
  const [linkText, setLinkText] = useState('');
  const [linkError, setLinkError] = useState('');
  const [uploadKey, setUploadKey] = useState(0); // a new key clears the Upload's own preview

  const useLink = () => {
    try {
      const link = parseAvatarLink(linkText);
      setLinkError('');
      setLinkText('');
      setUploadKey((k) => k + 1);
      onChange(link);
    } catch (err: any) {
      setLinkError(err.message);
    }
  };

  const remove = () => {
    setLinkText('');
    setLinkError('');
    setUploadKey((k) => k + 1);
    onChange(null);
  };

  return (
    <div className="profile-picture-picker" style={{ display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'stretch', width: '100%', textAlign: 'left' }}>
      {/* Row 1: the picture (when given), Upload and Remove: same buttons, same line, flush left */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        {leading}
        <Upload
          key={uploadKey}
          onUpload={(file) => {
            setLinkError('');
            onChange(file);
          }}
          disabled={disabled}
          showPreview={showFilePreview}
        />
        {hasPicture && (
          <button type="button" className="ui-button" disabled={disabled} onClick={remove}>
            Remove
          </button>
        )}
      </div>
      {/* Row 2: a link to a picture on any web site */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
        <input
          type="url"
          value={linkText}
          placeholder="Or paste a link to a picture"
          aria-label="Link to a picture"
          disabled={disabled}
          onChange={(e) => setLinkText(e.target.value)}
          style={{ flex: 1, minWidth: 0, height: 40, boxSizing: 'border-box', padding: '0 12px', border: '1px solid #ccc', borderRadius: 8, fontSize: 14 }}
        />
        <button type="button" className="ui-button" disabled={disabled || !linkText.trim()} onClick={useLink}>
          Use link
        </button>
      </div>
      {linkError && (
        <div role="alert" style={{ color: '#a11a1a', fontSize: 13 }}>
          {linkError}
        </div>
      )}
    </div>
  );
}

export default ProfilePicturePicker;
